import { useMemo, useState } from "react";
import {
  Box,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PersonSearch, Search } from "@mui/icons-material";
import { CircularLoading, NoDataWarning } from "@/components/common";
import { usePatientOrganizationContext } from "@/contexts/PatientOrganizationContext";
import { currentFeatures } from "@/configs/Settings";
import {
  mapPatientEntryToSearchRow,
  type ReissuePatientOption,
} from "./reissuePatientId";

interface ReissueSelectPatientStepProps {
  selectedPatient: ReissuePatientOption | null;
  /** Um clique no paciente já seleciona e avança para documentos. */
  onSelectAndContinue: (patient: ReissuePatientOption) => void;
}

/**
 * Passo 1 do wizard da agenda: busca e seleciona paciente
 * (lista da organização — mesmo contexto da busca do dashboard).
 */
export function ReissueSelectPatientStep({
  selectedPatient,
  onSelectAndContinue,
}: ReissueSelectPatientStepProps) {
  const { patients, patientOrganizationLoading } =
    usePatientOrganizationContext();
  const [search, setSearch] = useState("");

  const rows = useMemo(
    () => (patients || []).map(mapPatientEntryToSearchRow),
    [patients],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const qDigits = q.replace(/[^a-zA-Z0-9]/g, "");
    return rows.filter((row) => {
      const idDigits = row.identifier?.replace(/[^a-zA-Z0-9]/g, "") || "";
      return (
        row.name.toLowerCase().includes(q) ||
        (qDigits.length > 0 && idDigits.includes(qDigits))
      );
    });
  }, [rows, search]);

  return (
    <Stack spacing={2} width="100%">
      <Stack direction="row" spacing={1} alignItems="center">
        <PersonSearch color="primary" sx={{ mt: 0.5 }} />
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Selecione o paciente
          </Typography>
     
        </Box>
      </Stack>

      <TextField
        fullWidth
        variant="filled"
        label={
          currentFeatures.defaultApp
            ? "Busque por nome ou CPF e clique no paciente para continuar."
            : "Busque e clique no paciente para continuar."
        }
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search  />
            </InputAdornment>
          ),
        }}
        autoFocus
      />

      {patientOrganizationLoading ? (
        <Box py={4}>
          <CircularLoading />
        </Box>
      ) : !search.trim() ? (
        <NoDataWarning message="Digite para buscar um paciente." />
      ) : filteredRows.length === 0 ? (
        <NoDataWarning message="Nenhum paciente encontrado com os critérios informados." />
      ) : (
        <Paper
          variant="outlined"
          sx={{ borderRadius: 2, maxHeight: 280, overflow: "auto" }}
        >
          <List dense disablePadding>
            {filteredRows.slice(0, 50).map((row) => {
              const selected = selectedPatient?.patientId === row.patientId;
              return (
                <ListItemButton
                  key={row.patientResourceId || row.patientId}
                  selected={selected}
                  onClick={() => {
                    if (!row.patientId) return;
                    onSelectAndContinue(row);
                  }}
                  disabled={!row.patientId}
                >
                  <ListItemText
                    primary={row.name}
                    secondary={row.identifier || undefined}
                    primaryTypographyProps={{
                      fontWeight: selected ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Paper>
      )}
    </Stack>
  );
}
